import { createTheme } from '@mui/material/styles';

// Locker Room. Scoreboard logic: safety orange marks anything time-critical,
// and green/red are reserved strictly for win/lose so state reads before you
// have finished looking at it. Nothing else uses those two hues.
export const colors = {
    ground: '#14171C',
    surface: '#1D222A',
    raised: '#242B35',
    line: '#2C333E',
    ink: '#EDF0F4',
    inkSoft: '#BDC6D2',
    muted: '#78859A',
    safety: '#FF7A1A',
    win: '#3FBF7F',
    loss: '#E8543F',
    track: '#262D38',
} as const;

// The npm Archivo variable ships only a wght axis, so the condensed role uses
// Archivo Narrow rather than a wdth setting that would silently do nothing.
// Same superfamily either way: condensed for headings and figures, normal width
// for reading.
const condensed = '"Archivo Narrow", "Archivo Variable", "Arial Narrow", sans-serif';
const text = '"Archivo Variable", "Archivo", system-ui, sans-serif';
export const mono = '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace';

// Fingers, drinks, dark backyard.
export const MIN_TAP_TARGET = 44;

export const theme = createTheme({
    palette: {
        mode: 'dark',
        background: { default: colors.ground, paper: colors.surface },
        primary: { main: colors.safety, contrastText: colors.ground },
        success: { main: colors.win },
        error: { main: colors.loss },
        divider: colors.line,
        text: { primary: colors.ink, secondary: colors.inkSoft, disabled: colors.muted },
    },
    shape: { borderRadius: 3 },
    typography: {
        fontFamily: text,
        h1: {
            fontFamily: condensed,
            fontWeight: 700,
            textTransform: 'uppercase',
            lineHeight: 0.9,
            letterSpacing: '-0.01em',
        },
        h2: {
            fontFamily: condensed,
            fontWeight: 700,
            textTransform: 'uppercase',
            lineHeight: 1,
        },
        h3: {
            fontFamily: condensed,
            fontWeight: 700,
            textTransform: 'uppercase',
            lineHeight: 1.1,
            fontSize: '1.35rem',
        },
        button: { fontWeight: 700, letterSpacing: '0.04em' },
        overline: {
            fontFamily: mono,
            fontSize: '0.66rem',
            letterSpacing: '0.15em',
            fontWeight: 600,
            lineHeight: 1.6,
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                html: { WebkitTextSizeAdjust: '100%' },
                body: {
                    backgroundColor: colors.ground,
                    // Stops the page rubber-banding into the host chrome while
                    // scrolling a list mid-bet.
                    overscrollBehavior: 'contain',
                },
                // Removes the 300ms double-tap-zoom delay on controls without
                // disabling pinch-zoom for the whole document.
                'button, a, [role="button"]': { touchAction: 'manipulation' },
                ':focus-visible': { outline: `2px solid ${colors.safety}`, outlineOffset: 2 },
            },
        },
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                root: { minHeight: MIN_TAP_TARGET, paddingInline: 18 },
                sizeLarge: { minHeight: 52, fontSize: '1rem' },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: { minWidth: MIN_TAP_TARGET, minHeight: MIN_TAP_TARGET },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: { backgroundImage: 'none', border: `1px solid ${colors.line}` },
            },
        },
        MuiTextField: { defaultProps: { fullWidth: true, variant: 'outlined' } },
        MuiTab: { styleOverrides: { root: { minHeight: MIN_TAP_TARGET } } },
    },
});
